import React, { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { pdf } from '@react-pdf/renderer';
import OrderSummaryPdf from "../../components/shared/orders/OrderSummaryPdf";
import { visiblePhoneNumber } from "../../util/phoneNumber";
import ShopOrderModel from "../../components/Orders/ShopOrderModel";
import { buildDataForPDF } from "../../services/orderService";
import useCompleteMessageActions from "./inbox/actions/useCompleteMessageActions";
import { sharingInformationService } from "../../services/sharingInformationService";

export default function useOrderActionSender({ conversation, isChatClosed, subscribeToEvents = true }) {
  const { t } = useTranslation();

  const { customer } = useSelector((state) => state.inboxRightReducer);

  const { retailer_info } = useSelector(
    (reduxState) => reduxState.retailerUsersReducer
  );
  const { currentRetailerUser: currentRetailerUserInfo } = useSelector(
    (reduxState) => reduxState.retailerUsersReducer
  );

  const {
    sendAndAddMessage,
    sendAndAddMessageWithFiles,
  } = useCompleteMessageActions();

  const buildPdf = useCallback(async (order, selectedPaymentMethods = null) => {
    const visiblePhone = visiblePhoneNumber(customer.phone, currentRetailerUserInfo);

    const blobPdf = await pdf(
      <OrderSummaryPdf
        invoice={order}
        retailerInfo={retailer_info}
        customer={customer}
        phone={visiblePhone}
        paymentMethods={selectedPaymentMethods}
        paymentMethodsLabel={t('Inbox:sendPaymentLinks.availablePaymentMethods')}
      />
    ).toBlob();
    const orderId = order.sequential_number ? order.sequential_number : order.order_id;
    const filename = `${retailer_info.name}-orden-${orderId}.pdf`;

    return new File([blobPdf], filename, {
      type: 'application/pdf',
      lastModified: new Date().getTime()
    });
  }, [customer, retailer_info, t, currentRetailerUserInfo]);

  const generateOrderPdf = useCallback(async (order, selectedPaymentMethods = null) => {
    if (isChatClosed) {
      return null;
    }

    let file = null;
    try {
      if (order.web_id) {
        const res = await ShopOrderModel.getOne({ id: order.web_id });
        const newOrder = buildDataForPDF(res, retailer_info);
        file = await buildPdf(newOrder, selectedPaymentMethods);
      } else {
        file = await buildPdf(order, selectedPaymentMethods);
      }
    } catch (err) {
      console.error('Error generating PDF:', err);
    }

    return file;
  }, [buildPdf, isChatClosed, retailer_info]);

  const onSendPdf = useCallback(async (selectedPaymentMethods, formattedDateTime, order) => {
    const file_data = await generateOrderPdf(order, selectedPaymentMethods);
    await sendAndAddMessageWithFiles({
      type: 'file',
      template: false,
      platform: conversation?.platform,
      customer_id: conversation?.id,
      file_data
    });
  }, [generateOrderPdf, sendAndAddMessageWithFiles, conversation?.platform, conversation?.id]);

  const onSendPaymentLink = useCallback((message) => {
    sharingInformationService.setSubject({
      fillInputChatComponent: true,
      text: message
    });
    sharingInformationService.setSubject({});
  }, [sendAndAddMessage, conversation?.id, conversation?.platform]);

  useEffect(() => {
    if (!subscribeToEvents) return;
    const subscription$ = sharingInformationService.getSubject();
    const myObservable = subscription$.subscribe((data) => {
      if (data.sendPdfWithPaymentMethods && data.payload) {
        const { order, selectedPaymentMethods, formattedDateTime } = data.payload;
        onSendPdf(selectedPaymentMethods, formattedDateTime, order);
      }
    });
    return () => {
      myObservable.unsubscribe();
    };
  }, [onSendPdf, subscribeToEvents]);

  return {
    onSendPdf,
    onSendPaymentLink
  };
}